import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import IRider, { IRiderDetail, IRiderExtended } from '../../models/IRider';
import { IRound } from '../../models/IEvent';
import RiderServiceInstance from '../../services/RiderService';
import EventServiceInstance from '../../services/EventService';
import { Grid, Theme, Paper, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import ParticipantChart from './ParticipantChart';
import LapsHelperInstance from '../../services/LapsHelper';
import ParticipantSummary from './ParticipantSummary';
import ParticipantLaps from './ParticipantLaps';
import OntimeBreadcrumb from '../common/OntimeBreadcrumb';
import * as signalR from "@microsoft/signalr";
import { signalRTimingHubUrl } from '../../services/ServiceConstants';
import UserServiceInstance from '../../services/UserService';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            // minWidth: 650,
            tableLayout: 'auto'
        },
        container: {
            paddingTop: theme.spacing(10),
            paddingBottom: theme.spacing(4),
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2)
        },
        paper: {
            padding: theme.spacing(2),
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column',
        },
        chartContainer: {
            height: '400px',
            padding: theme.spacing(2),
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column',
        },
        summaryContainer: {
            marginTop: theme.spacing(3), // Adjust the value as needed
        }
    }));

function ParticipantDetail() {
    const { eventId, tagId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [rider, setRider] = useState<IRiderDetail>();
    const [rounds, setRounds] = useState<IRound[]>([]);
    const [selectedRound, setSelectedRound] = useState<string>(searchParams.get('round') ?? '');
    const hubConnectionRef = useRef<signalR.HubConnection>();
    const snackBar = useSnackbar();
    const classes = useStyles();
    const { t } = useTranslation('common');

    const fetchRider = useCallback(async (roundId?: string): Promise<IRiderDetail> => {
        if (!eventId) {
            throw new Error('EventId is required');
        }
        if (!tagId) {
            throw new Error('TagId is required');
        }

        const riderData = tagId
            ? await RiderServiceInstance.getRiderDetail(eventId, tagId)
            : await UserServiceInstance.getUserParticipation(eventId);

        if (!roundId) {
            return riderData;
        }

        const roundRiderData = await RiderServiceInstance.getRiderDetailForRound(eventId, tagId, roundId);
        return {
            ...riderData,
            ...roundRiderData,
            laps: roundRiderData.laps ?? [],
            bestLapId: roundRiderData.bestLapId ?? 0,
            bestTime: roundRiderData.bestTime ?? riderData.bestTime,
            lastTime: roundRiderData.lastTime ?? riderData.lastTime,
            averageTime: roundRiderData.averageTime ?? riderData.averageTime,
            averageMs: roundRiderData.averageMs ?? riderData.averageMs,
            lapCount: roundRiderData.lapCount ?? riderData.lapCount,
        };
    }, [eventId, tagId]);

    const fetchRiderDetailAndSubscribeSignalR = useCallback(async (riderData: IRiderDetail, hc: signalR.HubConnection) => {
        if (riderData.liveEvent === true) {
            hubConnectionRef.current = hc;
            try {
                await hc.start();
                if (hc.connectionId) {
                    const participantId = `${eventId}--${riderData.tagIdFriendly}`
                    hc.invoke("JoinRace", participantId);
                }

                hc.on("publishTime", (publishedRider: IRider) => {
                    if (!eventId) {
                        throw new Error('EventId is required');
                    }
                    RiderServiceInstance.getRiderRefresh(eventId, publishedRider.tagIdFriendly).then((riderExtended: IRiderExtended) => {
                        if (publishedRider) {
                            let mergedRider = { ...riderData, ...publishedRider, ...riderExtended };
                            setRider(mergedRider);
                        }
                    });
                });
            } catch (error) {
                console.error("Error starting SignalR connection: ", error);
                // todo: localize message
                snackBar.enqueueSnackbar("Error starting real-time lap updates", { variant: 'error' });
            }
        }
    }, [eventId, snackBar]);

    const unsubscribeSignalR = useCallback(() => {
        hubConnectionRef.current?.stop().then(() => console.log("participant connection closed"));
    }, []);

    useEffect(() => {
        let cancelled = false;

        unsubscribeSignalR();

        fetchRider(selectedRound || undefined)
            .then(riderData => {
                if (cancelled) {
                    return;
                }

                setRider(riderData);

                if (riderData.liveEvent === true && !selectedRound) {
                    // Builds the SignalR connection, mapping it to /timing
                    const hubConnection = new signalR.HubConnectionBuilder()
                        .withUrl(signalRTimingHubUrl)
                        .withAutomaticReconnect()
                        .configureLogging(signalR.LogLevel.Information)
                        .build();

                    fetchRiderDetailAndSubscribeSignalR(riderData, hubConnection)
                        .catch(reason => {
                            if (!cancelled) {
                                snackBar.enqueueSnackbar(reason, { variant: 'error' });
                            }
                        });
                }
            })
            .catch(reason => {
                if (!cancelled) {
                    snackBar.enqueueSnackbar(reason?.message ?? String(reason), { variant: 'error' });
                }
            });

        return () => {
            cancelled = true;
        };
    }, [fetchRider, fetchRiderDetailAndSubscribeSignalR, selectedRound, snackBar, unsubscribeSignalR]);

    useEffect(() => {
        if (!eventId) return;
        EventServiceInstance.getRounds(eventId).then(r => setRounds(r)).catch(() => {});
    }, [eventId]);

    useEffect(() => {
        window.addEventListener('unload', unsubscribeSignalR);
        return () => {
            window.removeEventListener('unload', unsubscribeSignalR);
            unsubscribeSignalR();
        }
    }, [unsubscribeSignalR]);

    const handleRoundChange = (roundId: string) => {
        setSelectedRound(roundId);
        if (roundId) {
            setSearchParams({ round: roundId });
        } else {
            setSearchParams({});
        }
    };

    const laps = rider?.laps ?? [];
    const idBestLap = rider?.bestLapId ?? 0;
    const bestLap = LapsHelperInstance.getBestLap(laps);
    const filteredLaps = LapsHelperInstance.filterBreakLaps(laps, bestLap?.timeMs);

    const eventName = rider?.eventName ?? '';
    const riderName = rider?.name ?? '';
    const eventRoute = `/event/${eventId}`;
    let nameMap: Record<string, string> = {};
    nameMap[eventName] = eventRoute;

    const hasRounds = rounds.length > 0;

    return (
        <Grid container className={classes.container} spacing={2} >
            <Grid item xs={12}>
                <OntimeBreadcrumb nameMap={nameMap} current={riderName} />
            </Grid>
            {hasRounds && (
                <Grid item xs={12}>
                    <Box>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel id="participant-round-filter-label">{t('rounds.filterByRound')}</InputLabel>
                            <Select
                                labelId="participant-round-filter-label"
                                id="participant-round-filter"
                                value={selectedRound}
                                label={t('rounds.filterByRound')}
                                onChange={(e) => handleRoundChange(e.target.value as string)}
                            >
                                <MenuItem value="">{t('rounds.allRounds')}</MenuItem>
                                {rounds.map(r => (
                                    <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Grid>
            )}
            <Grid item xs={12} className={classes.summaryContainer}>
                <Paper>
                    <ParticipantSummary rider={rider} />
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <ParticipantLaps laps={filteredLaps} idBestLap={idBestLap} />
            </Grid>
            <Grid item xs={12}>
                <Paper className={classes.chartContainer}>
                    <ParticipantChart
                        laps={filteredLaps}
                        bestLap={bestLap}
                        bestGroup={rider?.groupBest}
                        bestToday={rider?.eventBest}
                        bestCircuit={rider?.circuitBest}
                        averageTime={rider?.averageMs} />
                </Paper>
            </Grid>
        </Grid>
    );
}

export default ParticipantDetail;